/**
 * RestoID — the Continue-flow "extra profile" fields.
 * ---------------------------------------------------------------------------
 * A RestoID-shaped stand-in for the whole upstream `src/pages/Continue/ExtraProfileForm`
 * tree (`index.tsx` + `FullnameSubForm` + `AddressSubForm`), rendered by
 * `/continue/extra-profile`.
 *
 * The form stays DATA-DRIVEN: every control comes from the sign-in-experience's
 * `customProfileFields`, in its configured order, with its configured type, label,
 * description, required flag and config. Nothing about the field list is hard-coded here.
 *
 * What is reused from upstream, unchanged:
 *   - `useFieldLabel` — the `profile.<name>` i18n fallback when a field has no label;
 *   - `useValidateField` — every validation branch (date parse, regex, url, number range,
 *     text length, required) and every message it builds;
 *   - `AvatarUploadField` — the avatar control, with its crop modal, upload API, size /
 *     type errors and `onUploadingChange` reporting. Rebuilding that in RestoID markup
 *     would have meant reimplementing the upload flow, so it is imported as-is and the
 *     theme layer repaints it.
 *
 * What is restated, with the logic preserved line for line:
 *   - the same react-hook-form setup (`reValidateMode: 'onBlur'`, the page's
 *     `defaultValues`) shared through `FormProvider`, so the sub-forms still register
 *     into one form;
 *   - the same submit guard — a submit while the avatar is still uploading is dropped;
 *   - the same per-field `Controller` + `validate` rules, including the outer controller
 *     that upstream registers for an Address field;
 *   - FULLNAME: the same enabled-parts filter, the same per-part `required` /
 *     `validate` rules, and the same error block — every part message when any error is
 *     not a plain "required", otherwise ONE "required" line naming the parent field;
 *   - ADDRESS: the same enabled-parts filter, the same "render nothing when no part is
 *     enabled" guard, the same `address.formatted` computation (the enabled parts, in
 *     config order, joined with ", "), the same hidden `formatted` input when that part
 *     is not itself rendered, and the same collapsed-vs-listed error block;
 *   - the same `action.continue` CTA off `isSubmitting`, disabled while uploading, plus
 *     the hidden submit input.
 *
 * Deliberate presentation differences, all forced by the control swap:
 *   - text / number / url / regex fields become RestoID `Field`s; the optional-label
 *     suffix (`input.label_with_optional`) that upstream's `InputField` added is applied
 *     here instead, so an optional field still reads "Label (Optional)";
 *   - a DATE field becomes ONE masked `Field` instead of upstream's three-segment control.
 *     Separators are inserted as the user types, derived from the configured format, so
 *     the produced string is byte-identical to what `useValidateField` parses. A `Custom`
 *     format falls back to a plain field, exactly as upstream's DateField does;
 *   - a SELECT becomes a native `<select>` inside RestoID's field shell — the option
 *     labels (including the `profile.gender_options.*` fallback) are built exactly as
 *     upstream did. The OS picker is a better mobile control than a floating dropdown and
 *     is the only one RestoID has no primitive for;
 *   - a CHECKBOX becomes a RestoID check tile; the stored value is still the string
 *     `'true'` / `'false'` upstream writes. It also forwards `onBlur`, which upstream's
 *     `CheckboxField` never received — with `reValidateMode: 'onBlur'` that only means a
 *     blurred checkbox re-validates like every other control;
 *   - a field's `description` sits under the control, after the error line (RestoID's
 *     `Field` owns its error slot), where upstream put it before;
 *   - the FULLNAME parts stack one per row and the ADDRESS parts drop their half-width
 *     `locality` / `region` pairing. Upstream packed them two-up inside a 400px page;
 *     RestoID's card is narrower and its fields are 54px with the label ABOVE, so a
 *     two-up row leaves no room for either label. Order, names and rules are untouched;
 *   - `address.formatted` is recomputed from the part that just changed plus the watched
 *     rest, rather than from the watched object alone. Upstream reads `values` captured
 *     on the previous render and relies on react-hook-form mutating it in place; passing
 *     the new value in makes the join correct whether or not that holds, and produces
 *     the identical string when it does.
 */
import {
  CustomProfileFieldType,
  Gender,
  SupportedDateFormat,
  type CustomProfileField,
  type FieldOption,
  type UserProfile,
} from '@logto/schemas';
import { condString } from '@silverhand/essentials';
import { type CSSProperties, type ReactNode, useCallback, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as s from 'superstruct';

import AvatarUploadField from '@/components/InputFields/AvatarUploadField';
import useFieldLabel from '@/pages/Continue/ExtraProfileForm/use-field-label';
import useValidateField from '@/pages/Continue/ExtraProfileForm/use-validate-field';
import { addressFieldConfigGuard, fullnameFieldConfigGuard } from '@/types/guard';

import { Button, C, FONT, Field, Icon, SIZE, Stack } from './restoid';

/** The subset of a field / field-part config these controls read. */
type ControlConfig = {
  placeholder?: string;
  options?: FieldOption[];
  format?: string;
};

const labelStyle: CSSProperties = {
  display: 'block',
  color: C.sub,
  font: `500 13px/1 ${FONT}`,
  marginBottom: 9,
};

const noteStyle: CSSProperties = {
  marginTop: 8,
  color: C.faint,
  font: `400 12.5px/1.45 ${FONT}`,
};

const errorStyle: CSSProperties = {
  marginTop: 8,
  color: C.danger,
  font: `400 12.5px/1.45 ${FONT}`,
};

/** RestoID's field shell, for the two controls `Field` itself cannot host. */
const boxStyle = (danger?: boolean, isFocus?: boolean): CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  height: SIZE.field,
  padding: '0 14px 0 16px',
  background: C.field,
  borderRadius: 13,
  border: `1px solid ${danger ? C.danger : isFocus ? C.brand : C.line}`,
  boxShadow: isFocus
    ? `0 0 0 4px ${C.ring}, inset 0 1px 0 ${C.insetFocus}`
    : `inset 0 1px 0 ${C.inset}`,
  transition: 'border-color .15s ease, box-shadow .15s ease',
});

const FieldNote = ({ children }: { children: ReactNode }) => (
  <div style={noteStyle}>{children}</div>
);

const isGenderOptionKey = (key: string): key is Gender =>
  Object.values<string>(Gender).includes(key);

/* ------------------------------------------------------------------ select */
type SelectProps = {
  name: string;
  label?: string;
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
  value?: string;
  danger?: boolean;
  errorMessage?: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
};

const ProfileSelect = ({
  name,
  label,
  options,
  placeholder,
  value,
  danger,
  errorMessage,
  onChange,
  onBlur,
}: SelectProps) => {
  const [isFocus, setIsFocus] = useState(false);

  return (
    <div>
      {label ? (
        <label htmlFor={name} style={labelStyle}>
          {label}
        </label>
      ) : null}
      <div style={boxStyle(danger, isFocus)}>
        <select
          id={name}
          name={name}
          value={value ?? ''}
          aria-invalid={danger}
          style={{
            flex: 1,
            minWidth: 0,
            height: '100%',
            appearance: 'none',
            border: 'none',
            outline: 'none',
            background: 'transparent',
            color: value ? C.text : C.faint,
            font: `500 16px/1 ${FONT}`,
            cursor: 'pointer',
          }}
          onChange={(event) => {
            onChange(event.target.value);
          }}
          onFocus={() => {
            setIsFocus(true);
          }}
          onBlur={() => {
            setIsFocus(false);
            onBlur?.();
          }}
        >
          {/* The empty option keeps "nothing chosen" reachable — upstream's dropdown
              simply started blank. */}
          <option value="" style={{ background: C.page, color: C.text }}>
            {placeholder ?? ''}
          </option>
          {options.map((option) => (
            <option
              key={option.value}
              value={option.value}
              style={{ background: C.page, color: C.text }}
            >
              {option.label}
            </option>
          ))}
        </select>
        <span aria-hidden="true" style={{ color: C.faint, display: 'inline-flex' }}>
          <Icon name="chevR" size={18} style={{ transform: 'rotate(90deg)' }} />
        </span>
      </div>
      {danger && errorMessage ? <div style={errorStyle}>{errorMessage}</div> : null}
    </div>
  );
};

/* ---------------------------------------------------------------- checkbox */
const ProfileCheckbox = ({
  name,
  title,
  checked,
  onChange,
  onBlur,
}: {
  name: string;
  title?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  onBlur?: () => void;
}) => {
  const [isFocus, setIsFocus] = useState(false);

  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
      <input
        type="checkbox"
        name={name}
        checked={checked}
        style={{ position: 'absolute', width: 1, height: 1, opacity: 0 }}
        onChange={(event) => {
          onChange(event.target.checked);
        }}
        onFocus={() => {
          setIsFocus(true);
        }}
        onBlur={() => {
          setIsFocus(false);
          onBlur?.();
        }}
      />
      <span
        aria-hidden="true"
        style={{
          flex: '0 0 auto',
          width: 22,
          height: 22,
          borderRadius: 7,
          display: 'grid',
          placeItems: 'center',
          color: C.brand,
          background: checked ? C.tile : C.field,
          border: `1px solid ${checked ? C.tileLine : C.line}`,
          boxShadow: isFocus ? `0 0 0 4px ${C.ring}` : 'none',
          transition: 'background .15s ease, border-color .15s ease, box-shadow .15s ease',
        }}
      >
        {checked ? <Icon name="check" size={14} sw={2.6} /> : null}
      </span>
      <span style={{ color: C.text, font: `500 14px/1.4 ${FONT}` }}>{title}</span>
    </label>
  );
};

/* -------------------------------------------------------------------- date */
/** Mirrors upstream's `getDateFormatConfig`: the separator and the part widths. */
const getDateMask = (dateFormat?: string) => {
  if (
    dateFormat !== SupportedDateFormat.US &&
    dateFormat !== SupportedDateFormat.UK &&
    dateFormat !== SupportedDateFormat.ISO
  ) {
    return;
  }

  const separator = dateFormat === SupportedDateFormat.ISO ? '-' : '/';

  return { separator, lengths: dateFormat.split(separator).map((part) => part.length) };
};

/** Re-inserts the separators so the field always holds a value in the configured format. */
const applyDateMask = (
  raw: string,
  { separator, lengths }: { separator: string; lengths: number[] }
) => {
  const total = lengths.reduce((sum, length) => sum + length, 0);
  let rest = raw.replace(/\D/g, '').slice(0, total);
  const parts: string[] = [];

  for (const length of lengths) {
    if (!rest) {
      break;
    }
    parts.push(rest.slice(0, length));
    rest = rest.slice(length);
  }

  return parts.join(separator);
};

/** The configured placeholder, falling back to a hint when the config leaves it empty. */
const getPlaceholder = (configured?: string, fallback?: string) => {
  if (configured) {
    return configured;
  }

  return fallback;
};

/* ------------------------------------------------- one primitive control */
type ControlProps = {
  name: string;
  type?: CustomProfileFieldType;
  label?: string;
  description?: string;
  required?: boolean;
  config?: ControlConfig;
  value?: string;
  danger?: boolean;
  errorMessage?: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
};

/**
 * The RestoID counterpart of upstream's `PrimitiveProfileInputField`: it routes a field's
 * configured TYPE to a control, exactly as upstream routed it to
 * SelectField / CheckboxField / DateField / InputField.
 */
const ProfileControl = ({
  name,
  type,
  label,
  description,
  required,
  config,
  value,
  danger,
  errorMessage,
  onChange,
  onBlur,
}: ControlProps) => {
  const { t } = useTranslation();
  // Upstream added this suffix inside InputField / DateField / SelectField (never on the
  // checkbox), so it is applied here for the same three.
  const labelWithOptionalSuffix =
    label && (required ? label : condString(t('input.label_with_optional', { label })));

  if (type === CustomProfileFieldType.Select) {
    const options =
      config?.options?.map(({ value, label }) => {
        if (!label && isGenderOptionKey(value)) {
          return { value, label: t(`profile.gender_options.${value}`) };
        }
        return { value, label: label ?? value };
      }) ?? [];

    return (
      <div>
        <ProfileSelect
          name={name}
          label={labelWithOptionalSuffix}
          options={options}
          placeholder={config?.placeholder}
          value={value}
          danger={danger}
          errorMessage={errorMessage}
          onChange={onChange}
          onBlur={onBlur}
        />
        {description ? <FieldNote>{description}</FieldNote> : null}
      </div>
    );
  }

  if (type === CustomProfileFieldType.Checkbox) {
    return (
      <div>
        <ProfileCheckbox
          name={name}
          title={label}
          checked={value === 'true'}
          onBlur={onBlur}
          onChange={(checked) => {
            onChange(checked ? 'true' : 'false');
          }}
        />
        {description ? <FieldNote>{description}</FieldNote> : null}
      </div>
    );
  }

  const dateMask = type === CustomProfileFieldType.Date ? getDateMask(config?.format) : undefined;
  // The console writes an EMPTY placeholder by default, so a masked date falls back to
  // showing its own format ("dd/MM/yyyy") as the hint — upstream got that for free from
  // its per-segment placeholders (DD / MM / YYYY).
  const placeholder = getPlaceholder(config?.placeholder, dateMask ? config?.format : undefined);

  return (
    <div>
      {/* The control stays a plain TEXT input, exactly as upstream's `InputField` was.
          Typing it as `url` / `number` would hand the browser a constraint of its own,
          and a native validation bubble would fire on submit INSTEAD of upstream's
          `error.general_invalid` / `error.invalid_min_max_input` — i.e. it would
          swallow the very error branch `useValidateField` exists to produce.
          `inputMode` is safe (it only picks the on-screen keyboard) but is set for the
          masked date alone: a Number field can be configured with a negative
          `minValue`, and the numeric keypad has no minus key. */}
      <Field
        name={name}
        label={labelWithOptionalSuffix}
        placeholder={placeholder}
        inputMode={dateMask ? 'numeric' : 'text'}
        value={value ?? ''}
        danger={danger}
        errorMessage={errorMessage}
        onChange={(next) => {
          onChange(dateMask ? applyDateMask(next, dateMask) : next);
        }}
        onBlur={onBlur}
      />
      {description ? <FieldNote>{description}</FieldNote> : null}
    </div>
  );
};

/* --------------------------------------------------------- fullname block */
type FullnameFormType = Pick<UserProfile, 'givenName' | 'middleName' | 'familyName'>;

const FullnameSubForm = ({ field }: { field: CustomProfileField }) => {
  const { t } = useTranslation();
  const getFieldLabel = useFieldLabel();
  const validateField = useValidateField();

  const { name, label, description, config } = field;
  s.assert(config, fullnameFieldConfigGuard);

  const {
    control,
    formState: { errors },
  } = useFormContext<FullnameFormType>();

  const enabledParts = useMemo(() => config.parts.filter(({ enabled }) => enabled), [config.parts]);
  const fullnameErrors = Object.entries(errors).filter(([errorKey]) =>
    enabledParts.some(({ name }) => name === errorKey)
  );
  const hasNonRequiredErrors = fullnameErrors.some(([_, error]) => error.type !== 'required');

  return (
    <Stack gap={14}>
      {enabledParts.map((part) => (
        <Controller
          key={part.name}
          name={part.name}
          control={control}
          rules={{
            required:
              part.required &&
              t('error.general_required', { types: [getFieldLabel(part.name, part.label)] }),
            validate: (value) => validateField(value, part),
          }}
          render={({ field: { onBlur, onChange, value } }) => (
            <ProfileControl
              name={part.name}
              type={part.type}
              label={part.label ?? t(`profile.${part.name}`)}
              description={part.description}
              required={part.required}
              config={part.config}
              value={value ?? ''}
              danger={Boolean(errors[part.name])}
              onChange={onChange}
              onBlur={onBlur}
            />
          )}
        />
      ))}
      {description ? <FieldNote>{description}</FieldNote> : null}
      {fullnameErrors.length > 0 ? (
        <div role="alert" style={{ color: C.danger, font: `400 12.5px/1.45 ${FONT}` }}>
          {hasNonRequiredErrors ? (
            fullnameErrors.map(([errorKey, error]) => (
              <p key={errorKey} style={{ margin: 0 }}>
                {error.message}
              </p>
            ))
          ) : (
            <p style={{ margin: 0 }}>
              {t('error.general_required', { types: [getFieldLabel(name, label)] })}
            </p>
          )}
        </div>
      ) : null}
    </Stack>
  );
};

/* ---------------------------------------------------------- address block */
type AddressSubFormType = Pick<UserProfile, 'address'>;

/**
 * The address value is `{ streetAddress?, locality?, region?, postalCode?, country?,
 * formatted? }`. `formatted` is either configured as its own part and rendered, or
 * computed from the other parts and kept in a hidden input.
 */
const AddressSubForm = ({ field }: { field: CustomProfileField }) => {
  const { t } = useTranslation();
  const validateField = useValidateField();
  const getFieldLabel = useFieldLabel();

  const {
    control,
    watch,
    setValue,
    register,
    formState: { errors },
  } = useFormContext<AddressSubFormType>();

  const { description, config } = field;

  const enabledParts = useMemo(() => {
    s.assert(config, addressFieldConfigGuard);
    return config.parts.filter(({ enabled }) => enabled);
  }, [config]);

  const values = watch('address');

  const setFormattedValue = useCallback(
    (changedName: string, changedValue: string) => {
      const formatted = enabledParts
        .map(({ name }) => (name === changedName ? changedValue : values?.[name]))
        .filter(Boolean)
        .join(', ');
      setValue('address.formatted', formatted);
    },
    [enabledParts, setValue, values]
  );

  if (enabledParts.length === 0) {
    return null;
  }

  const hasNonRequiredErrors = Object.entries(errors.address ?? {}).some(
    ([_, error]) => typeof error === 'object' && error.type !== 'required'
  );

  return (
    <Stack gap={14}>
      {enabledParts.map((part) => (
        <Controller
          key={part.name}
          name={`address.${part.name}`}
          control={control}
          rules={{
            required:
              part.required &&
              t('error.general_required', {
                types: [getFieldLabel(`address.${part.name}`, part.label)],
              }),
            validate: (value) => validateField(value, part),
          }}
          render={({ field: { onBlur, onChange, value } }) => (
            <ProfileControl
              name={`address.${part.name}`}
              type={part.type}
              label={part.label ?? t(`profile.address.${part.name}`)}
              description={part.description}
              required={part.required}
              config={part.config}
              value={value ?? ''}
              danger={Boolean(errors.address?.[part.name])}
              onBlur={onBlur}
              onChange={(next) => {
                onChange(next);
                if (part.name !== 'formatted') {
                  setFormattedValue(part.name, next);
                }
              }}
            />
          )}
        />
      ))}
      {!enabledParts.some(({ name }) => name === 'formatted') && (
        <input {...register('address.formatted')} hidden />
      )}
      {description ? <FieldNote>{description}</FieldNote> : null}
      {errors.address ? (
        <div role="alert" style={{ color: C.danger, font: `400 12.5px/1.45 ${FONT}` }}>
          {hasNonRequiredErrors ? (
            Object.entries(errors.address).map(([errorKey, error]) => (
              <p key={errorKey} style={{ margin: 0 }}>
                {typeof error === 'object' && 'message' in error ? error.message : String(error)}
              </p>
            ))
          ) : (
            <p style={{ margin: 0 }}>
              {t('error.general_required', { types: [t('profile.address.formatted')] })}
            </p>
          )}
        </div>
      ) : null}
    </Stack>
  );
};

/* ---------------------------------------------------------------- the form */
type Props = {
  customProfileFields: CustomProfileField[];
  defaultValues?: Record<string, unknown>;
  onSubmit: (values: Record<string, unknown>) => Promise<void>;
};

const ContinueProfileFields = ({ customProfileFields, defaultValues, onSubmit }: Props) => {
  const { t } = useTranslation();
  const getFieldLabel = useFieldLabel();
  const validateField = useValidateField();
  const [isAvatarUploading, setIsAvatarUploading] = useState(false);
  const methods = useForm<Record<string, unknown>>({
    reValidateMode: 'onBlur',
    defaultValues,
  });

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = methods;

  const submit = handleSubmit(async (value) => {
    if (isAvatarUploading) {
      return;
    }

    await onSubmit(value);
  });

  return (
    <FormProvider {...methods}>
      <form onSubmit={submit}>
        <Stack gap={18}>
          {customProfileFields.map((profileField) => {
            if (profileField.type === CustomProfileFieldType.Fullname) {
              return <FullnameSubForm key={profileField.name} field={profileField} />;
            }

            const { name, type, label, description, required } = profileField;

            return (
              <Controller
                key={name}
                control={control}
                name={name}
                rules={{
                  validate: (value) =>
                    validateField(value, { ...profileField, description: condString(description) }),
                }}
                render={({ field: { onBlur, onChange, value } }) => {
                  if (type === CustomProfileFieldType.Address) {
                    return <AddressSubForm field={profileField} />;
                  }

                  if (name === 'avatar' && type === CustomProfileFieldType.Url) {
                    s.assert(value, s.optional(s.string()));

                    return (
                      <AvatarUploadField
                        name={name}
                        label={label || getFieldLabel(name)}
                        description={condString(description)}
                        isRequired={required}
                        value={value}
                        errorMessage={errors[name]?.message}
                        onChange={onChange}
                        onBlur={onBlur}
                        onUploadingChange={setIsAvatarUploading}
                      />
                    );
                  }

                  s.assert(value, s.optional(s.string()));

                  return (
                    <ProfileControl
                      name={name}
                      type={type}
                      label={label || getFieldLabel(name)}
                      description={condString(description)}
                      required={required}
                      config={profileField.config}
                      value={value}
                      danger={Boolean(errors[name])}
                      errorMessage={errors[name]?.message}
                      onChange={onChange}
                      onBlur={onBlur}
                    />
                  );
                }}
              />
            );
          })}
          <Button
            title={t('action.continue')}
            htmlType="submit"
            isLoading={isSubmitting}
            isDisabled={isAvatarUploading}
          />
        </Stack>
        <input hidden type="submit" disabled={isAvatarUploading} />
      </form>
    </FormProvider>
  );
};

export default ContinueProfileFields;
